import React, { useContext, useState, useEffect } from 'react';
import Loading from 'tbk-components/src/components/Loading';
import Modal from 'tbk-components/src/components/Modal';
import { IRecruiterDataTableItem } from './RecruiterDataTable';
import withAppContext from 'tbk-components/src/hooks/withAppContext';
import { IImage } from 'tbk-components/src/components/Image';
import { ILink } from 'tbk-components/src/components/Button';
import CareersProfileCard from '../CareersProfileCard';
import Tooltip from 'bootstrap/js/dist/tooltip';

export interface IRecruiterApplicant extends IRecruiterDataTableItem {
	user_id?: number;
	job_title?: string;
	was_referred?: boolean;
	referrer?: string;
	profile_picture?: IImage;
	about_yourself?: string;
	skills?: string;
	address_1?: string;
	address_2?: string;
	city?: string;
	province?: string;
	country?: string;
	postal_code?: string;
	willing_to_relocate?: boolean;
	relocation_notes?: string;
	job_preferences?: string[];
	language_preference?: string;
	communication_preference?: string;
	display_name?: string;
}

export interface IRecruiterApplicantModalI18n {
	about_yourself?: string;
	referred_to_calian_by?: string;
	referred_to_calian?: string;
	skills?: string;
	preferences?: string;
	job_preferences?: string;
	communication_preference?: string;
	language_preference?: string;
	willing_to_relocate?: string;
	communication_preference_labels?: {
		email?: string;
		phone?: string;
	};
	language_preference_labels?: {
		en?: string;
		fr?: string;
	};
}

export const defaultI18n: IRecruiterApplicantModalI18n = {
	about_yourself: 'About Me',
	referred_to_calian_by: 'This applicant was referred to Calian by',
	referred_to_calian: 'This applicant was referred to Calian',
	skills: 'Skills',
	preferences: 'Preferences',
	job_preferences: 'Job Type',
	communication_preference: 'Communication',
	language_preference: 'Language',
	willing_to_relocate: 'This applicant is willing to relocate',
	communication_preference_labels: {
		email: 'Email',
		phone: 'Phone',
	},
	language_preference_labels: {
		en: 'English',
		fr: 'French',
	},
};

export interface IRecruiterApplicantModalProps {
	applicant: IRecruiterApplicant;
	i18n?: IRecruiterApplicantModalI18n;
	onClose: () => void;
}

const RecruiterApplicantModal: React.FC<IRecruiterApplicantModalProps> = withAppContext(({
	applicant,
	i18n = defaultI18n,
	onClose = () => {},
	context,
}) => {
	const { Api } = useContext( context );
	const [ record, setRecord ] = useState( null );
	const [ loading, setLoading ] = useState( false );
	useEffect( () => {
		setLoading( true );
		setRecord( applicant );
		Api.getRecruiterApplicant( applicant.user_id ).then( setRecord ).finally( () => setLoading( false ) );
	}, [applicant] );
	useEffect(() => {
		if ( ! record?.relocation_notes ) {
			return;
		}
		// Timeout is necessary because the modal takes a slight bit of processing to appear.
		setTimeout(() => {
			const tooltipTriggerList = document.getElementById('applicant-modal').querySelectorAll('[data-bs-toggle="tooltip"]');
			const tooltipList = [...tooltipTriggerList].map(
				(tooltipTriggerEl) => new Tooltip(tooltipTriggerEl),
			);
		});
	}, [record?.relocation_notes]);

	return <Modal
		onCancel={ onClose }
		headerClassNames={['border-0 p-4 p-lg-5']}
		modalClassNames={['modal-dialog-centered modal-xl']}
		bodyClassNames={['modal-body px-3 py-0 px-lg-5 px-xl-9']}
		id="applicant-modal"
	>
		{ ( loading || !record )
			? <Loading/>
			: <>
				<div className="row align-items-start mb-5">
					<div className="col-12 col-md-6">
						<CareersProfileCard
							className="calian-career-profile-card card d-flex flex-row flex-lg-column justify-content-between border-0"
							profileImage={record.profile_picture}
							profileName={record.display_name}
							profileTitle={record.job_title}
							editable={false}
						/>
						<div className="d-flex justify-content-start">
							{record.resume && <a href={record.resume.href} target="_blank" className="d-inline-block fs-7 px-0 me-5 mt-3">{record.resume.label}</a>}
							{record.cover_letter && <a href={record.cover_letter.href} target="_blank" className="d-inline-block fs-7 px-0 me-5 mt-3">{record.cover_letter.label}</a>}
							{record.linkedin && <a href={record.linkedin} target="_blank" className="d-inline-block fs-7 px-0 me-5 mt-3">LinkedIn</a>}
						</div>
						{record.was_referred && <div className="col-12">
							<p className="fst-italic fs-7">
								{record.referrer ? i18n.referred_to_calian_by : i18n.referred_to_calian}
								{record.referrer && <>{' '}{record.referrer}</>}
							</p>
						</div>}
					</div>
					<div className="col-12 col-md-6">
						{record.city && <div className="text-start text-md-end fs-7">
							{record.address_1 && <>{record.address_1}<br/></>}
							{record.address_2 && <>{record.address_2}<br/></>}
							{record.city && <>{record.city}, </>}
							{record.province && <>{record.province}, </>}
							{record.country && <>{record.country}<br/></>}
							{record.postal_code && <>{record.postal_code}<br/></>}
							{record.email && <><a href={`mailto:${record.email}`}>{record.email}</a><br/></>}
							{record.phone && <><a href={`tel:${record.phone.replace(/[^0-9]/,'')}`}>{record.phone}</a><br/></>}
						</div>}
						{record.willing_to_relocate && <div className="text-start text-md-end fs-7 fst-italic">
							{record.relocation_notes
								? <span
									className="d-inline-flex justify-content-start align-items-center"
									data-bs-toggle="tooltip"
									data-bs-custom-class="calian-career-dashboard-filter__tooltip border bg-white rounded-3"
									data-bs-placement="top"
									data-bs-title={record.relocation_notes}
								>
									{i18n.willing_to_relocate}
									<i className="icon icon-question btn d-inline-flex justify-content-center align-items-center fs-8 ms-1 p-0"></i>
								</span>
								: i18n.willing_to_relocate
							}
						</div>}
					</div>
				</div>

				{record.about_yourself && <div className="row mb-5">
					<h3>{i18n.about_yourself}</h3>
					<div style={{whiteSpace:'pre-line'}}>{record.about_yourself}</div>
				</div>}
				{record.skills && <div className="row mb-5">
					<h3>{i18n.skills}</h3>
					<div style={{whiteSpace:'pre-line'}}>{record.skills}</div>
				</div>}
				{(record.job_preferences?.length || record.language_preference || record.communication_preference) && <div className="row mb-5">
					<h3>{i18n.preferences}</h3>
					{record.job_preferences?.length ? <div className="col-12 col-lg-4">
						<h4>{i18n.job_preferences}</h4>
						<ul>
							{record.job_preferences.map( ( pref, i ) => <li key={i}>{pref}</li> )}
						</ul>
					</div> : null}
					{record.communication_preference && <div className="col-12 col-lg-4">
						<h4>{i18n.communication_preference}</h4>
						<ul>
							<li>{i18n.communication_preference_labels[record.communication_preference]}</li>
						</ul>
					</div>}
					{record.language_preference && <div className="col-12 col-lg-4">
						<h4>{i18n.language_preference}</h4>
						<ul>
							<li>{i18n.language_preference_labels[record.language_preference]}</li>
						</ul>
					</div>}
				</div>}
		</>}
	</Modal>;
});

export default RecruiterApplicantModal;